/**
 * Truncates a name to a maximum length for table/list overviews, appending "[...]".
 * Returns the value unchanged when it is empty or within the limit.
 * @param {string|null|undefined} name - Name to truncate
 * @param {number} [maxLength=30] - Maximum length before truncating
 * @returns {string|null|undefined}
 */
export function truncateName(name, maxLength = 30) {
	if (!name || name.length <= maxLength) return name;
	return name.slice(0, maxLength) + '[...]';
}

/**
 * Extracts the initials from a full name (e.g. "Max Mustermann" -> "MM").
 * Returns '?' when no name is provided.
 * @param {string|null|undefined} name - Full name
 * @returns {string} One- or two-letter uppercase initials, or '?'
 */
export function getInitials(name) {
	if (!name) return '?';
	const parts = name.trim().split(/\s+/);
	if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
	return parts[0][0]?.toUpperCase() ?? '?';
}

/**
 * Initials for a user avatar, preferring the profile name and falling back to the
 * email local part ('max.mustermann@…' -> 'MM'), since a fresh account has no name set.
 *
 * @param {string|null|undefined} fullName - Name from the user's profile
 * @param {string|null|undefined} email - Account email, used only as a fallback
 * @returns {string} One- or two-letter uppercase initials, or '?'
 */
export function getUserInitials(fullName, email) {
	if (fullName) return getInitials(fullName);
	if (!email) return '?';
	return getInitials(email.split('@')[0].split('.').join(' '));
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validates that a string looks like a well-formed email address.
 * @param {string|null|undefined} email - The email address to validate
 * @returns {boolean} True when the value matches the basic email format
 */
export function isValidEmail(email) {
	if (!email || typeof email !== 'string') return false;
	return EMAIL_REGEX.test(email.trim());
}
